import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaSearch,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import { API_URL } from "../../config/apiConfig";

const PopularCities = () => {
  const { user } = useSelector((state) => state.auth);
  const [cities, setCities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [currentCity, setCurrentCity] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    order: 0,
  });

  // Get auth token
  const getAuthToken = () => {
    return user?.token;
  };


  // Fetch cities from API
  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      setIsLoading(true);
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token available");
      }

      const response = await axios.get(`${API_URL}/popular-cities/admin`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        timeout: 10000, // 10 second timeout
      });

      setCities(response.data);
    } catch (error) {
      console.error("Error fetching popular cities:", error);

      if (error.code === "ECONNABORTED") {
        console.error("Request timeout - please check your connection");
      } else if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const message = error.response.data?.message || "Server error";
        console.error(`Server error ${status}:`, message);
      } else if (error.request) {
        // Network error - no response received
        console.error("Network error:", error.request);
      } else {
        // Other error
        console.error("Error:", error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (city = null) => {
    if (city) {
      setFormData({
        name: city.name,
        order: city.order,
      });
      setCurrentCity(city);
    } else {
      setFormData({
        name: "",
        order: 0,
      });
      setCurrentCity(null);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentCity(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "order" ? parseInt(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("City name is required!");
      return;
    }

    try {
      const token = getAuthToken();
      const cityData = {
        name: formData.name.trim(),
        order: formData.order,
      };

      if (currentCity) {
        // Update existing city
        await axios.put(
          `${API_URL}/popular-cities/admin/${currentCity._id}`,
          cityData,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success("City updated successfully!");
      } else {
        // Add new city
        await axios.post(`${API_URL}/popular-cities/admin`, cityData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("City added successfully!");
      }

      handleCloseModal();
      fetchCities();
    } catch (error) {
      console.error("Error saving city:", error);
      toast.error(error.response?.data?.message || "Failed to save city");
    }
  };

  const handleToggleStatus = async (cityId, currentStatus) => {
    try {
      const token = getAuthToken();
      await axios.put(
        `${API_URL}/popular-cities/admin/${cityId}`,
        {
          isActive: !currentStatus,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("City status updated successfully!");
      fetchCities();
    } catch (error) {
      console.error("Error updating city status:", error);
      toast.error("Failed to update city status");
    }
  };

  const handleDelete = async (cityId, cityName) => {
    if (window.confirm(`Are you sure you want to delete "${cityName}"?`)) {
      try {
        const token = getAuthToken();
        await axios.delete(`${API_URL}/popular-cities/admin/${cityId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("City deleted successfully!");
        fetchCities();
      } catch (error) {
        console.error("Error deleting city:", error);
        toast.error("Failed to delete city");
      }
    }
  };

  const filteredCities = cities.filter((city) =>
    city.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-0">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Popular Cities Management
          </h1>
          <p className="text-gray-600">
            Manage cities displayed in the footer section
          </p>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search cities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaPlus /> Add City
            </button>
          </div>
        </div>

        {/* Cities Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    Order
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    City Name
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    Created
                  </th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-600">Loading cities...</p>
                    </td>
                  </tr>
                ) : filteredCities.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-gray-500">
                      No cities found
                    </td>
                  </tr>
                ) : (
                  filteredCities.map((city) => (
                    <tr
                      key={city._id}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-3 px-4">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                          {city.order}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900">
                        {city.name}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() =>
                            handleToggleStatus(city._id, city.isActive)
                          }
                          className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                            city.isActive
                              ? "bg-green-100 text-green-800 hover:bg-green-200"
                              : "bg-red-100 text-red-800 hover:bg-red-200"
                          }`}
                        >
                          {city.isActive ? <FaToggleOn /> : <FaToggleOff />}
                          {city.isActive ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {new Date(city.createdAt).toLocaleDateString("en-IN")}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleOpenModal(city)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(city._id, city.name)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  {currentCity ? "Edit City" : "Add New City"}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter city name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Order
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Lower numbers appear first
                    </p>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {currentCity ? "Update" : "Add"} City
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default PopularCities;
